import { useState } from 'react';
import api from 'api';
import { useApiData } from 'lib/api';
import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import { getThemeOptions, themes as builtInThemes } from 'lib/reader/themes';
import { Button, FlexContainer, LinkButton } from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader
} from 'components/common/box';
import { Checkbox, Form, FormGroup, Select } from 'components/common/form';
import { TextInput } from 'components/common/form/common';
import { SiteSettingsThemesCreate, SiteSettingsThemesLink } from '.';

const SiteSettingsReaderThemes = () => {
  const { dispatch } = useDispatch();
  const { messages, serverConfig, theme } = useReduxState();

  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const { data, loading, updateData } = useApiData({
    url: '/admin/server-themes?type=reader'
  });

  let readerThemes = data?.themes || [];

  readerThemes = readerThemes.sort((a, b) =>
    a.updatedAt > b.updatedAt ? -1 : 1
  );

  if (search) {
    readerThemes = readerThemes.filter((theme) =>
      theme.title.toLowerCase().includes(search.toLowerCase())
    );
  }

  const save = async (values) => {
    try {
      setSaving(true);
      await api.put(`/admin/server-config`, {
        data: {
          readerBuiltInThemes: values.readerBuiltInThemes,
          readerCustomThemeId: values.readerCustomThemeId || null,
          ...(!values.readerCustomThemeId
            ? {
                readerDefaultTheme: values.readerDefaultTheme
              }
            : {})
        }
      });
      const response = await api.get('/admin/server-config');
      dispatch(actions.setServerConfig(response.data.data));
      setSaving(false);
      dispatch(
        actions.addNotice({
          text: messages.settingsSavedSuccessfully,
          type: 'success'
        })
      );
    } catch (err) {
      setSaving(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  const { readerBuiltInThemes, readerCustomThemeId, readerDefaultTheme } =
    serverConfig;

  const readerThemeOptions = getThemeOptions({ messages });

  return (
    <div className="site-themes">
      <Box loading={loading} padding={0} title={messages.siteReaderThemes}>
        {!loading && readerThemes ? (
          <div className="content">
            <div className="save">
              <Form
                initialValues={{
                  readerBuiltInThemes,
                  readerCustomThemeId: readerCustomThemeId || 0,
                  readerDefaultTheme
                }}
                onSubmit={save}
              >
                {({ formState }) => (
                  <div>
                    <FormGroup label={messages.siteReaderThemesCustom}>
                      <FlexContainer style={{ gap: 10 }}>
                        <Select
                          field="readerCustomThemeId"
                          id={`reader-custom-theme-id`}
                          isSearchable={true}
                          options={[
                            {
                              label: messages.siteReaderThemesCustomNone,
                              value: 0
                            },
                            ...readerThemes.map((readerTheme) => ({
                              label: readerTheme.title,
                              value: readerTheme.id
                            }))
                          ]}
                          style={{ width: 300, maxWidth: '100%' }}
                        />
                        {formState.values?.readerCustomThemeId ? (
                          <LinkButton
                            category="lighter"
                            href={`/admin/settings/reader-themes/${readerCustomThemeId}`}
                            size="medium"
                            text={'Edit current theme'}
                          />
                        ) : null}
                      </FlexContainer>
                    </FormGroup>

                    {!formState.values.readerCustomThemeId ? (
                      <>
                        <FormGroup label={messages.siteReaderThemesDefault}>
                          <Select
                            field="readerDefaultTheme"
                            id={`reader-default-theme`}
                            options={readerThemeOptions.map((theme) => ({
                              label: theme.label,
                              value: theme.value
                            }))}
                            style={{ width: 300, maxWidth: '100%' }}
                          />
                        </FormGroup>

                        <FormGroup label={messages.siteReaderThemesBuiltIn}>
                          {readerThemeOptions.map((builtInThemeOption) => (
                            <Checkbox
                              key={builtInThemeOption.id}
                              field={`readerBuiltInThemes.${builtInThemeOption.value}`}
                              id={`reader-built-in-themes-${builtInThemeOption.id}`}
                              label={builtInThemeOption.label}
                              style={{ marginBottom: 5 }}
                            />
                          ))}
                        </FormGroup>
                      </>
                    ) : null}

                    <FormGroup style={{ marginTop: 25 }}>
                      <Button
                        category="success"
                        loading={saving}
                        text={messages.commonSave}
                        type="submit"
                      />
                    </FormGroup>
                  </div>
                )}
              </Form>
            </div>
          </div>
        ) : null}
      </Box>

      <Box
        headerContent={
          <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
            <div style={{ width: 240 }}>
              <TextInput
                placeholder={messages.commonSearch}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <SiteSettingsThemesCreate type="reader" updateThemes={updateData} />
          </FlexContainer>
        }
        loading={loading}
        padding={0}
        style={{ marginTop: 20 }}
        title="Available themes"
      >
        <BoxTable>
          <BoxTableHeader>
            <BoxTableCell>{messages.commonTitle}</BoxTableCell>
            <BoxTableCell>{messages.commonUpdated}</BoxTableCell>
            <BoxTableCell>{messages.commonStatus}</BoxTableCell>
            <BoxTableCell />
          </BoxTableHeader>

          {readerThemes.map((readerTheme) => (
            <SiteSettingsThemesLink
              as={`/admin/settings/reader-themes/${readerTheme.id}`}
              href="/admin/settings/reader-themes/[themeId]"
              color={
                readerTheme.data.headerBackgroundColor ||
                builtInThemes.blue.headerBackgroundColor
              }
              id={readerTheme.id}
              key={readerTheme.id}
              themeEnabledStatus={
                readerCustomThemeId === readerTheme.id
                  ? 'Default'
                  : readerTheme.data.enableWithAttributeConditions?.enabled
                  ? 'Conditional'
                  : 'Disabled'
              }
              title={readerTheme.title}
              updatedAt={readerTheme.updatedAt}
              updateThemes={updateData}
            />
          ))}
        </BoxTable>
      </Box>

      <style jsx>{`
        .save {
          padding: 15px;
        }
        .themes {
          border-top: 1px solid ${theme.lightGrey};
          margin-top: 30px;
        }
      `}</style>
    </div>
  );
};

export default SiteSettingsReaderThemes;
